import React, { useRef } from "react";

import { Box, ButtonBase, alpha, useTheme } from "@mui/material";

import {
  focusRing,
  reducedMotion,
} from "@dashboard/components/themes/themeMotion";

const STEP_BY_KEY: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/** Arrow keys move the choice between a group's radios, as in a native one. */
export const useRadioArrowKeys = <T,>(
  values: ReadonlyArray<T>,
  onChange: (value: T) => void
) => {
  const radios = useRef<Array<HTMLButtonElement | null>>([]);

  return {
    radioRef: (index: number) => (el: HTMLButtonElement | null) => {
      radios.current[index] = el;
    },
    onKeyDown: (event: React.KeyboardEvent, index: number) => {
      const step = STEP_BY_KEY[event.key];
      if (!step) return;
      event.preventDefault();
      const next = (index + step + values.length) % values.length;
      radios.current[next]?.focus();
      onChange(values[next]);
    },
  };
};

/** A radiogroup drawn as a segmented control, with native arrow-key movement. */
export const SegmentedControl = <T extends string>({
  label,
  options,
  value,
  onChange,
  fullWidth = false,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  fullWidth?: boolean;
}) => {
  const { palette } = useTheme();
  const oscar = palette.oscar.light;
  const { radioRef, onKeyDown } = useRadioArrowKeys(
    options.map((option) => option.value),
    onChange
  );

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{
        display: fullWidth ? "grid" : "inline-grid",
        gridAutoFlow: "column",
        gridAutoColumns: "1fr",
        gap: 0.5,
        p: 0.5,
        borderRadius: "10px",
        background: oscar.surface.gray,
      }}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            ref={radioRef(index)}
            role="radio"
            aria-checked={checked}
            aria-label={option.label}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            sx={[
              {
                height: 32,
                px: 2,
                borderRadius: "7px",
                fontSize: 13,
                whiteSpace: "nowrap",
                background: checked ? oscar.surface.clear : "transparent",
                boxShadow: checked
                  ? `0 1px 2px ${alpha(palette.common.black, 0.12)}`
                  : "none",
                transition: "background 150ms ease-out",
              },
              focusRing(),
              reducedMotion,
            ]}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};
